import useRemoveEnergyPerformanceCertificate from '@/api/mutations/energyPerformanceCertificate/useRemoveEnergyPerformanceCertificate';
import useGetMyRegionPreferences from '@/api/queries/me/useGetMyRegionPreferences';
import { ProjectByIdQuery } from '@/gql/graphql';
import { SyncEnergyPerformanceCertificateButton } from '@/ui/pages/projectPage/components/PropertyPanel/SyncEnergyPerformanceCertificateButton';
import {
  Accordion,
  Button,
  Divider,
  ManageableItemsList,
  Modal,
} from '@payaca/ambrosia';

import { EpcRatingBadge } from '@payaca/components/epcRatingBadge';
import {
  DateFormats,
  getInternationalDateFormatByLocale,
} from '@payaca/helpers/internationalHelper';
import UntitledIcon from '@payaca/untitled-icons';
import { formatDate } from 'date-fns';
import { FC, useState } from 'react';
import CollapsibleCard from '../CollapsibleCard';
import { GridElement } from '../GridElement';

type EnergyPerformanceCertificateCardProps = {
  projectId: string;
  siteAddressPublicId: string;
  energyPerformanceCertificate: ProjectByIdQuery['projectById']['addresses']['site'][number]['energyPerformanceCertificates'][number];
};

const EnergyPerformanceCertificateCard: FC<
  EnergyPerformanceCertificateCardProps
> = (props) => {
  const {
    projectId,
    siteAddressPublicId,
    energyPerformanceCertificate: epc,
  } = props;

  const [isConfirmRemoveModalOpen, setIsConfirmRemoveModalOpen] =
    useState(false);

  const { mutate: removeEpc, isPending: isRemovingEpc } =
    useRemoveEnergyPerformanceCertificate({ projectId });

  const handleRemove = () => {
    removeEpc(
      {
        addressId: siteAddressPublicId,
      },
      {
        onSuccess: () => {
          setIsConfirmRemoveModalOpen(false);
        },
      }
    );
  };

  return (
    <>
      <CollapsibleCard
        title="Energy performance certificate (EPC)"
        summary={
          epc.energyPerformance.currentEnergyRating ? (
            <EpcRatingBadge
              rating={epc.energyPerformance.currentEnergyRating}
              energyEfficiency={epc.energyPerformance.currentEnergyEfficiency}
              size="sm"
            />
          ) : undefined
        }
        actions={
          <div className="space-x-2">
            <SyncEnergyPerformanceCertificateButton
              projectId={projectId}
              addressId={siteAddressPublicId}
              variant="secondary"
              size="sm"
            >
              Refresh <UntitledIcon name="refresh-cw-01" className="w-4 h-4" />
            </SyncEnergyPerformanceCertificateButton>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setIsConfirmRemoveModalOpen(true)}
            >
              Remove <UntitledIcon name="trash-01.3" className="w-4 h-4" />
            </Button>
          </div>
        }
      >
        <div className="flex">
          {/* Ratings section */}
          <div className="space-y-4 border-r-1 pr-6">
            {/* Current rating */}
            {epc.energyPerformance.currentEnergyRating && (
              <GridElement title="Current rating">
                <EpcRatingBadge
                  rating={epc.energyPerformance.currentEnergyRating}
                  energyEfficiency={
                    epc.energyPerformance.currentEnergyEfficiency
                  }
                />
              </GridElement>
            )}

            {/* Potential rating */}
            {epc.energyPerformance.potentialEnergyRating && (
              <GridElement title="Potential rating">
                <EpcRatingBadge
                  rating={epc.energyPerformance.potentialEnergyRating}
                  energyEfficiency={
                    epc.energyPerformance.potentialEnergyEfficiency
                  }
                />
              </GridElement>
            )}
          </div>

          {/* oxlint-disable-next-line payaca/no-tailwind-arbitrary-values -- Auto-fill grid, so the column count follows the card width */}
          <div className="pl-6 grid grid-cols-[repeat(auto-fill,minmax(250px,1fr))] gap-4 flex-1">
            <GridElement title="Lodged">
              <FormattedDate
                date={epc.metadata.lodgementDate}
                format={DateFormats.MID}
              />
            </GridElement>
            <GridElement title="Inspected">
              <FormattedDate
                date={epc.metadata.inspectionDate}
                format={DateFormats.MID}
              />
            </GridElement>
            <GridElement title="Property type">
              {epc.property.propertyType}
            </GridElement>
            <GridElement title="Built form">
              {epc.property.builtForm}
            </GridElement>
            <GridElement title="Construction age">
              {epc.property.constructionAgeBand}
            </GridElement>
            <GridElement title="Tenure">
              <p className="capitalize">{epc.property.tenure}</p>
            </GridElement>
            <GridElement title="Total floor area">
              {epc.property.totalFloorArea} m²
            </GridElement>
            <GridElement title="Habitable rooms">
              {epc.property.numberHabitableRooms}
            </GridElement>
            <GridElement title="Certificate number">
              {epc.metadata.certificateNumber}
            </GridElement>
            <GridElement title="Environmental impact">
              {epc.energyPerformance.currentEnvironmentImpact} (current) /{' '}
              {epc.energyPerformance.potentialEnvironmentImpact} (potential)
            </GridElement>
          </div>
        </div>

        <Divider />

        <Accordion title="Features in this property" panelClassName="space-y-4">
          <ManageableItemsList>
            <ManageableItemsList.Table
              uniqueKey="id"
              items={[
                {
                  id: 'walls',
                  title: 'Walls',
                  description: epc.buildingComponents.walls.description,
                  rating: epc.buildingComponents.walls.energyEfficiency,
                },
                {
                  id: 'roof',
                  title: 'Roof',
                  description: epc.buildingComponents.roof.description,
                  rating: epc.buildingComponents.roof.energyEfficiency,
                },
                {
                  id: 'floor',
                  title: 'Floor',
                  description: epc.buildingComponents.floor.description,
                  rating: epc.buildingComponents.floor.energyEfficiency,
                },
                {
                  id: 'windows',
                  title: 'Windows',
                  description: epc.buildingComponents.windows.description,
                  rating: epc.buildingComponents.windows.energyEfficiency,
                },
                {
                  id: 'main-heating',
                  title: 'Main heating',
                  description: epc.buildingComponents.mainHeating.description,
                  rating: epc.buildingComponents.mainHeating.energyEfficiency,
                },
                {
                  id: 'main-heating-controls',
                  title: 'Main heating controls',
                  description:
                    epc.buildingComponents.mainHeatingControls.description,
                  rating:
                    epc.buildingComponents.mainHeatingControls.energyEfficiency,
                },
                {
                  id: 'secondary-heating',
                  title: 'Secondary heating',
                  description:
                    epc.buildingComponents.secondaryHeating.description,
                  rating:
                    epc.buildingComponents.secondaryHeating.energyEfficiency,
                },
                {
                  id: 'hot-water',
                  title: 'Hot water',
                  description: epc.buildingComponents.hotWater.description,
                  rating: epc.buildingComponents.hotWater.energyEfficiency,
                },
                {
                  id: 'lighting',
                  title: 'Lighting',
                  description: epc.buildingComponents.lighting.description,
                  rating: epc.buildingComponents.lighting.energyEfficiency,
                },
              ]}
            >
              <ManageableItemsList.Table.Column
                header="Feature"
                field="title"
                className="align-top"
              />
              <ManageableItemsList.Table.Column
                header="Description"
                field="description"
                className="whitespace-normal align-top"
              />
              <ManageableItemsList.Table.Column
                header="Rating"
                field="rating"
                className="align-top"
              />
            </ManageableItemsList.Table>
          </ManageableItemsList>
        </Accordion>

        <Divider />

        <Accordion
          title="Additional property details"
          panelClassName="space-y-4"
        >
          {/* oxlint-disable-next-line payaca/no-tailwind-arbitrary-values -- Auto-fill grid, so the column count follows the card width */}
          <div className="grid grid-cols-[repeat(auto-fill,minmax(250px,1fr))] gap-4 mt-4">
            <GridElement title="Unique property reference number (UPRN)">
              {epc.property.uprn}
            </GridElement>
            <GridElement title="UPRN source">
              {epc.property.uprnSource}
            </GridElement>
            <GridElement title="Local authority">
              <p>{epc.property.localAuthority}</p>
              <p>{epc.property.localAuthorityLabel}</p>
            </GridElement>
            <GridElement title="Constituency">
              <p>{epc.property.constituency}</p>
              <p>{epc.property.constituencyLabel}</p>
            </GridElement>
            <GridElement title="Number heated rooms">
              {epc.property.numberHeatedRooms}
            </GridElement>
            <GridElement title="Number open fireplaces">
              {epc.property.numberOpenFireplaces}
            </GridElement>
            <GridElement title="Flat top storey">
              {epc.property.flatTopStorey === 'Y' ? 'Yes' : 'No'}
            </GridElement>
            <GridElement title="Flat storey count">
              {epc.property.flatStoreyCount}
            </GridElement>
            <GridElement title="Floor level">
              {epc.property.floorLevel}
            </GridElement>
            <GridElement title="Extension count">
              {epc.property.extensionCount}
            </GridElement>
            <GridElement title="Transaction type">
              <p className="capitalize">{epc.property.transactionType}</p>
            </GridElement>
          </div>

          <Divider />

          <h4>Energy performance details</h4>
          {/* oxlint-disable-next-line payaca/no-tailwind-arbitrary-values -- Auto-fill grid, so the column count follows the card width */}
          <div className="grid grid-cols-[repeat(auto-fill,minmax(250px,1fr))] gap-4 mt-4">
            <GridElement title="Current energy consumption">
              {epc.energyPerformance.currentEnergyConsumption} kWh/m² per year
            </GridElement>
            <GridElement title="Potential energy consumption">
              {epc.energyPerformance.potentialEnergyConsumption} kWh/m² per year
            </GridElement>
            <GridElement title="Current CO2 emissions">
              {epc.energyPerformance.currentCo2Emissions} tonnes per year
            </GridElement>
            <GridElement title="Potential CO2 emissions">
              {epc.energyPerformance.potentialCo2Emissions} tonnes per year
            </GridElement>
            <GridElement title="Current CO2 emissions per floor area">
              {epc.energyPerformance.currentCo2EmissionsPerFloorArea} kg/m²
            </GridElement>
          </div>

          <Divider />

          <h4>Energy costs</h4>
          {/* oxlint-disable-next-line payaca/no-tailwind-arbitrary-values -- Auto-fill grid, so the column count follows the card width */}
          <div className="grid grid-cols-[repeat(auto-fill,minmax(250px,1fr))] gap-4 mt-4">
            <GridElement title="Current heating cost">
              £{epc.costs.currentHeatingCost}
            </GridElement>
            <GridElement title="Potential heating cost">
              £{epc.costs.potentialHeatingCost}
            </GridElement>
            <GridElement title="Current hot water cost">
              £{epc.costs.currentHotWaterCost}
            </GridElement>
            <GridElement title="Potential hot water cost">
              £{epc.costs.potentialHotWaterCost}
            </GridElement>
            <GridElement title="Current lighting cost">
              £{epc.costs.currentLightingCost}
            </GridElement>
            <GridElement title="Potential lighting cost">
              £{epc.costs.potentialLightingCost}
            </GridElement>
          </div>

          <Divider />

          <h4>Technical specifications</h4>
          {/* oxlint-disable-next-line payaca/no-tailwind-arbitrary-values -- Auto-fill grid, so the column count follows the card width */}
          <div className="grid grid-cols-[repeat(auto-fill,minmax(250px,1fr))] gap-4 mt-4">
            <GridElement title="Main fuel">
              <p className="capitalize">{epc.technical.mainFuel}</p>
            </GridElement>
            <GridElement title="Main heating controls">
              {epc.technical.mainHeatingControls}
            </GridElement>
            <GridElement title="Floor height">
              {epc.technical.floorHeight} m
            </GridElement>
            <GridElement title="Glazed type">
              <p className="capitalize">{epc.technical.glazedType}</p>
            </GridElement>
            <GridElement title="Glazed area">
              {epc.technical.glazedArea}
            </GridElement>
            <GridElement title="Multi glaze proportion">
              {epc.technical.multiGlazeProportion}%
            </GridElement>
            <GridElement title="Low energy lighting">
              {epc.technical.lowEnergyLighting}%
            </GridElement>
            <GridElement title="Low energy fixed light count">
              {epc.technical.lowEnergyFixedLightCount}
            </GridElement>
            <GridElement title="Fixed lighting outlets count">
              {epc.technical.fixedLightingOutletsCount}
            </GridElement>
            <GridElement title="Energy tariff">
              {epc.technical.energyTariff}
            </GridElement>
            <GridElement title="Mains gas available">
              {epc.technical.mainsGasFlag === 'Y' ? 'Yes' : 'No'}
            </GridElement>
            <GridElement title="Solar water heating">
              {epc.technical.solarWaterHeatingFlag === 'Y' ? 'Yes' : 'No'}
            </GridElement>
            <GridElement title="Mechanical ventilation">
              <p className="capitalize">
                {epc.technical.mechanicalVentilation}
              </p>
            </GridElement>
            <GridElement title="Photo supply">
              {epc.technical.photoSupply} kW
            </GridElement>
            <GridElement title="Wind turbine count">
              {epc.technical.windTurbineCount}
            </GridElement>
            <GridElement title="Heat loss corridor">
              <p className="capitalize">{epc.technical.heatLossCorridor}</p>
            </GridElement>
            <GridElement title="Unheated corridor length">
              {epc.technical.unheatedCorridorLength} m²
            </GridElement>
          </div>
        </Accordion>
      </CollapsibleCard>
      <Modal
        isOpen={isConfirmRemoveModalOpen}
        onClose={() => setIsConfirmRemoveModalOpen(false)}
        title="Remove EPC?"
      >
        <Modal.Body>
          <p>
            Are you sure you want to remove this energy performance certificate?
            You can search for it again later if needed.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Modal.Footer.Actions>
            <Button
              variant="secondary"
              onClick={() => setIsConfirmRemoveModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              colour="negative"
              onClick={handleRemove}
              isProcessing={isRemovingEpc}
            >
              Remove
            </Button>
          </Modal.Footer.Actions>
        </Modal.Footer>
      </Modal>
    </>
  );
};

// TODO put this into shared components.
const FormattedDate: FC<{
  date: Date | string | null | undefined;
  format?: DateFormats;
}> = (props) => {
  const { date, format = DateFormats.SHORT } = props;

  const { data: accountRegionPreferences } = useGetMyRegionPreferences();

  if (!date) return null;

  return formatDate(
    date,
    getInternationalDateFormatByLocale(format, accountRegionPreferences?.locale)
  );
};

export default EnergyPerformanceCertificateCard;
